// Scene overrides (camera + lighting + background) for the Jockey inline
// demo experiences. Currently applies to /jockeydemo/1722/inline (Enamor 301)
// and /jockeydemo/1842/inline (Enamor 303). Drives both:
//   - BottomMenu's open/close card camera animation
//   - ModelViewerContainer's resetCamera (tap-outside) + scene-prop wiring
// Lighting/bg values mirror /poc/picker.html so the demo matches the picker preview.

// Single distance for all Jockey-1842 states: the open-card framing is the
// target everywhere — initial load, card open, card closed, and after shape /
// placement / size changes via resetCamera.
// Distance is tuned for the taller (430 × 870) iframe wrapper — at the prior
// 3.97m distance the avatar lost relative scale when we extended the canvas
// vertically. ~3.32m brings the avatar back to the same height-fill ratio.
const DIST_M          = 3.32 * 1.05 * 1.05 * 1.05;       // ~3.84m (zoomed out 3 notches from 3.32)
// 1820 zooms in 2 notches (each notch = 1.05× distance) — so DIST_M / 1.05²
const DIST_M_1820     = DIST_M / 1.05 / 1.05;            // ~3.48m
const TARGET_Y_DROP_M = 0.25;                      // drop framing (~2 more inches lower)
// 1820 raises the camera 2 notches (each notch ≈ 0.05m / 2 inches) so we
// subtract less from the base target Y — net effect: camera tilts up,
// avatar sits lower in the viewport (more chest area visible).
const TARGET_Y_DROP_M_1820 = TARGET_Y_DROP_M - 0.10;     // 0.15m
const MAX_ORBIT       = "auto 120deg 5m";          // lifted so DIST_M isn't clamped

// Card-open state lives in BottomMenu but is read by ModelViewerContainer's
// resetCamera. Plain module-scoped boolean keeps the cross-component coupling
// trivial — no context plumbing needed for one Jockey-specific tweak.
let cardOpen = false;
export const setInlineCardOpen = (open) => { cardOpen = !!open; };
export const isInlineCardOpen  = () => cardOpen;

export const getIframeSearchParam = (key) => {
  try {
    return new URLSearchParams(window.location.search).get(key);
  } catch (e) {
    return null;
  }
};

export const getHostPagePath = () => {
  try {
    return typeof window !== "undefined"
      ? (window.parent !== window ? window.parent.location.pathname : window.location.pathname)
      : "";
  } catch (e) {
    return "";
  }
};

export const getOuterBrand = () => getIframeSearchParam("outerBrand");

// Matches any of the Jockey inline demo routes:
// /jockeydemo/{1722|1842|216|1816|1820|MI10}/inline and the tone-test
// path /jockeydemo/1842/skin-natural. Shopify embeds are cross-origin,
// so the iframe cannot read parent.location — the app extension passes
// ?jockeyInline=true (generic) or ?jockeyInline=1820|FE93|1841|216|MI10
// (picker-stage variants — also imply broader Jockey inline behavior).
export const isJockeyInline = () => {
  const forcedInline = getIframeSearchParam("jockeyInline");
  if (forcedInline === "true" || forcedInline === "1") return true;
  // Picker-stage variants (1820/FE93/1841/216/MI10) imply Jockey inline
  // too — delegate to isJockey1820Inline so the TAE can ship a single
  // param and get both the broader Jockey UX and the picker-stage
  // HDR/camera.
  if (isJockey1820Inline()) return true;
  return /\/jockeydemo\/(1722|1842|216|1816|1820|1841|FE93|MI10|JW11)\/(inline|skin-natural)/.test(getHostPagePath());
};
// Back-compat alias — older call sites still import this name.
export const isJockey1842Inline = isJockeyInline;

// Narrower predicate for /jockeydemo/1820/inline. The 1820 PDP demo runs
// with the POC-picker stage setup (pillars_1k.hdr as env + skybox,
// exposure 1.0, neutral tone-mapping) rather than the flat-white HDRI
// used by the other inline routes — the bra reads more realistic with
// directional/architectural light.
export const isJockey1820Inline = () => {
  const forcedInline = getIframeSearchParam("jockeyInline");
  if (
    forcedInline === "1820" ||
    forcedInline === "FE93" ||
    forcedInline === "1841" ||
    forcedInline === "216" ||
    forcedInline === "MI10" ||
    forcedInline === "JW11"
  ) return true;
  return /\/jockeydemo\/(1820|FE93|1841|216|MI10|JW11)\/inline/.test(getHostPagePath());
};

// Narrower predicate for /jockeydemo/FE93/inline. Uses the same camera +
// stage props as 1820 but with a custom HDRI for the IBL/skybox so the
// FE93 cotton bra can be lit independently from 1820's mesh-panel SKU.
export const isJockeyFE93Inline = () => {
  const forcedInline = getIframeSearchParam("jockeyInline");
  if (forcedInline === "FE93") return true;
  return /\/jockeydemo\/FE93\/inline/.test(getHostPagePath());
};

// Narrower predicate for the natural-skin-tone test path. Used by the
// SkinToneSelector to swap its swatch list and by AppModule to seed the
// Controls panel with the "Natural" lighting preset.
export const isJockeySkinNatural = () => {
  const forcedTone = getIframeSearchParam("jockeySkinTone");
  if (forcedTone === "natural") return true;
  return /\/jockeydemo\/1842\/skin-natural/.test(getHostPagePath());
};

const patchOrbitDistance = (orbitStr, distM) => {
  if (typeof orbitStr !== "string") return orbitStr;
  const parts = orbitStr.trim().split(/\s+/);
  if (parts.length < 3) return orbitStr;
  parts[2] = `${distM}m`;
  return parts.join(" ");
};

const patchTargetY = (targetStr, yDeltaM) => {
  if (typeof targetStr !== "string") return targetStr;
  const parts = targetStr.trim().split(/\s+/);
  if (parts.length < 3) return targetStr;
  const yMatch = parts[1].match(/^(-?\d+(?:\.\d+)?)m$/);
  if (!yMatch) return targetStr;
  parts[1] = `${(parseFloat(yMatch[1]) - yDeltaM).toFixed(3)}m`;
  return parts.join(" ");
};

// Returns a cameraMode patched with the Jockey-1842 inline overrides, applied
// on top of the supplied base (e.g. CAMERA_MODE.enamor.BODY). The cardOpen
// signal is ignored — we want the same framing across all states.
export const applyJockey1842Overrides = (base) => {
  if (!base) return base;
  // 1820 inline reuses this override path but pulls the camera in 2 notches
  // closer and raises the framing 2 notches.
  const is1820 = isJockey1820Inline();
  const dist  = is1820 ? DIST_M_1820 : DIST_M;
  const yDrop = is1820 ? TARGET_Y_DROP_M_1820 : TARGET_Y_DROP_M;
  return {
    ...base,
    modelOrbit:     patchOrbitDistance(base.modelOrbit, dist),
    modelTarget:    patchTargetY(base.modelTarget, yDrop),
    maxCameraOrbit: MAX_ORBIT,
  };
};

// Lighting + background mirrored from public/poc/picker.html so this experience
// matches the picker preview exactly.
//   environment-image="neutral" exposure="1.0" shadow-intensity="0.5"
//   .stage { background: #0e0e0e }   (and no skybox → bg shows through)
export const JOCKEY_1842_STAGE_BG = "#ffffff";
export const applyJockey1842SceneProps = (base) => ({
  ...base,
  // model-viewer's "neutral" built-in IBL has a slight warm horizon
  // gradient that tints the lit avatar warm. Swap to the project's
  // explicitly white HDRI so the IBL light is neutral-cool.
  environmentImage: "/environment/HDRI_White_02A.hdr",
  skyboxImage:      "",
  exposure:         "2.0",
  shadowIntensity:  "0.5",
  toneMapping:      "neutral", // picker omits tone-mapping → model-viewer default
});

// Substitute selectedSkybox for jockey-1842-inline. The default skybox
// (modelViewerList[0]) puts /environment/pillars_1k.hdr into selectedSkybox, and
// ModelViewer has an imperative useEffect that writes that to mv.environmentImage
// — overriding our React props. Returning this object instead causes that same
// effect to land on the picker values.
export const JOCKEY_1842_SKYBOX = {
  environment: "/environment/HDRI_White_02A.hdr",
  skybox:      "",
  exposure:    2.0,
  id:          "__jockey_1842_inline__",
};
